import _ from 'lodash';
import { getPageElement, isPageElement, omitStaticElementsAndHiddenElements } from 'crazyEditor/core/elements/utils/elementStaticUtils';
import { MOBILE_MIN_HEIGHT, DESKTOP_MIN_HEIGHT, MOBILE_WIDTH} from 'crazyEditor/core/constants/editorConstants';
import { VIEW_MODES } from 'crazyEditor/core/constants/editorConstants';
import { PAGE_ELEMENT_ID } from 'crazyEditor/core/elements/utils/elementStaticUtils';
import { KNOB_DIRECTIONS } from 'crazyEditor/core/constants/editorConstants';
import { getElementDefinitionProps } from 'crazyEditor/core/elements/utils/elementDefinitionUtils';
import { elementTypes } from 'crazyEditor/components/Elements/index';

function getMinHeightByViewMode(viewMode) {
  return viewMode === VIEW_MODES.MOBILE ? MOBILE_MIN_HEIGHT : DESKTOP_MIN_HEIGHT;
}

function getBottomedElement(elements) {
  return _.sortBy(omitStaticElementsAndHiddenElements(elements), (element) => element.layout.y + element.layout.height).pop();
}

function getNormalizedDirection(direction) {
  let normalizedDirection = direction;

  switch (normalizedDirection) {
    case KNOB_DIRECTIONS.TOP_LEFT:
      normalizedDirection = KNOB_DIRECTIONS.TOP_MIDDLE;
      break;
    case KNOB_DIRECTIONS.TOP_RIGHT:
      normalizedDirection = KNOB_DIRECTIONS.TOP_MIDDLE;
      break;
    case KNOB_DIRECTIONS.BOTTOM_LEFT:
      normalizedDirection = KNOB_DIRECTIONS.MIDDLE_LEFT;
      break;
    case KNOB_DIRECTIONS.BOTTOM_RIGHT:
      normalizedDirection = KNOB_DIRECTIONS.MIDDLE_RIGHT;
      break;
    default:
      return direction;
  }
  return normalizedDirection;
}

function getResizedLayoutBasedOnDirection(direction, elementLayout, deltaX, deltaY, keepAspectRatio) {
  let height;
  let width;
  let aspectRatio = elementLayout.aspectRatio;
  const normalizedDirection = aspectRatio ? getNormalizedDirection(direction) : direction;
  if (!aspectRatio && keepAspectRatio && ([KNOB_DIRECTIONS.BOTTOM_LEFT, KNOB_DIRECTIONS.BOTTOM_RIGHT, KNOB_DIRECTIONS.TOP_LEFT, KNOB_DIRECTIONS.TOP_RIGHT].indexOf(normalizedDirection) !== -1)) {
    aspectRatio = elementLayout.width / elementLayout.height;
  }

  switch (normalizedDirection) {

    case KNOB_DIRECTIONS.TOP_LEFT:
      return {
        width: Math.round(elementLayout.width - deltaX),
        height: Math.round(elementLayout.height - deltaY),
        x: Math.round(elementLayout.x + deltaX),
        y: Math.round(elementLayout.y + deltaY)
      };
    case KNOB_DIRECTIONS.TOP_RIGHT:
      return {
        width: Math.round(elementLayout.width + deltaX),
        height: Math.round(elementLayout.height - deltaY),
        y: Math.round(elementLayout.y + deltaY)
      };
    case KNOB_DIRECTIONS.MIDDLE_LEFT:
      width = Math.floor(elementLayout.width - deltaX);
      height = elementLayout.height;
      return {
        width: width,
        height: aspectRatio ? Math.floor(width / aspectRatio) : height,
        x: Math.floor(elementLayout.x + deltaX)
      };
    case KNOB_DIRECTIONS.MIDDLE_RIGHT:
      width = Math.floor(elementLayout.width + deltaX);
      height = elementLayout.height;
      return {
        width: width,
        height: aspectRatio ? Math.floor(width / aspectRatio) : height
      };
    case KNOB_DIRECTIONS.BOTTOM_LEFT:
      return {
        width: Math.round(elementLayout.width - deltaX),
        height: Math.round(elementLayout.height + deltaY),
        x: Math.round(elementLayout.x + deltaX)
      };
    case KNOB_DIRECTIONS.BOTTOM_RIGHT:
      return {
        width: Math.round(elementLayout.width + deltaX),
        height: Math.round(elementLayout.height + deltaY)
      };
    case KNOB_DIRECTIONS.TOP_MIDDLE:
      height = Math.floor(elementLayout.height - deltaY);
      width = elementLayout.width;
      return {
        y: Math.floor(elementLayout.y + deltaY),
        height: height,
        width: aspectRatio ? Math.floor(height * aspectRatio) : width
      };
    case KNOB_DIRECTIONS.BOTTOM_MIDDLE:
      height = Math.floor(elementLayout.height + deltaY);
      width = elementLayout.width;
      return {
        height: height,
        width: aspectRatio ? Math.floor(height * aspectRatio) : width
      };
    default:
      return {
        width: elementLayout.width,
        height: elementLayout.height
      };
  }
}

function getLayoutForPageElement(viewMode, elements, newElementLayout) {
  let viewHeight = getMinHeightByViewMode(viewMode);
  const bottomedElement = getBottomedElement(elements);
  if (bottomedElement) {
    viewHeight = Math.max(viewHeight, bottomedElement.layout.y + bottomedElement.layout.height);
  }
  newElementLayout.height = Math.max(newElementLayout.height, viewHeight);
  const {minWidth, maxWidth} = getElementDefinitionProps(elementTypes.PageElement, {isMobile: viewMode === VIEW_MODES.MOBILE});
  newElementLayout.width = Math.max(newElementLayout.width, minWidth);
  newElementLayout.width = Math.min(newElementLayout.width, maxWidth);
  return newElementLayout;
}

function getElementSizeBasedOnBoundaries(viewMode, elements, element, newElementLayout, elementLayout, pageElement) {
  if (newElementLayout.width <= 1) {
    newElementLayout.width = 1;
  }
  if (newElementLayout.height <= 1) {
    newElementLayout.height = 1;
  }
  if (isPageElement(element.id)) {
    _.assign(elementLayout, getLayoutForPageElement(viewMode, elements, newElementLayout, elementLayout));
  } else {
    if ((newElementLayout.height + (newElementLayout.y || elementLayout.y) <= pageElement.layout.height) || (element.layout.width < newElementLayout.width)
      || getElementDefinitionProps(element.type).autoHeight) {
      _.assign(elementLayout, newElementLayout);
    } else {
      elementLayout.overflowing = true;
    }
  }
  return elementLayout;
}

function getUpdatedElementSizeByDirection(viewState, state, action, elementId) {
  const elements = state[viewState].elements;
  const staticElements = state[viewState].staticElements;
  const element = elements[elementId] || staticElements[elementId];
  const elementLayout = _.assign({}, element.layout);
  const viewMode = action.viewMode;
  const pageElement = getPageElement(staticElements);

  const newElementLayout = getResizedLayoutBasedOnDirection(action.direction, elementLayout, action.deltaX, action.deltaY, action.keepAspectRatio);

  return getElementSizeBasedOnBoundaries(viewMode, elements, element, newElementLayout, elementLayout, pageElement);
}

function getUpdatedElementSize(viewState, state, action, elementId) {
  const elements = state[viewState].elements;
  const staticElements = state[viewState].staticElements;
  const element = elements[elementId] || staticElements[elementId];
  const elementLayout = _.assign({}, element.layout);
  const viewMode = action.viewMode;
  const pageElement = getPageElement(staticElements);

  const newElementLayout = {
    width: _.isNumber(action.width) && action.width > 0 ? action.width : elementLayout.width,
    height: _.isNumber(action.height) && action.height > 0 ? action.height : elementLayout.height
  };

  if (elementLayout.aspectRatio) {
    if (action.width) {
      newElementLayout.height = Math.floor(newElementLayout.width / elementLayout.aspectRatio);
    } else if (action.height) {
      newElementLayout.width = Math.floor(newElementLayout.height * elementLayout.aspectRatio);
    }
  }

  return getElementSizeBasedOnBoundaries(viewMode, elements, element, newElementLayout, elementLayout, pageElement);
}

export {
  getUpdatedElementSizeByDirection,
  getUpdatedElementSize
};
